import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Instagram, Podcast, Mail, Loader2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { LINKS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Build Coaching",
  "BUILD Online",
  "Food Freedom Coaching",
  "PT Mentorship",
  "Something else",
];

const inputCls =
  "w-full border border-ink/15 bg-white px-4 py-3.5 text-sm placeholder:text-ink/35 transition-colors duration-300 focus:border-ocean rounded-none";

const Contact = () => {

  const [searchParams] = useSearchParams();
  const interestParam = searchParams.get("interest");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    interest: INTERESTS.includes(interestParam) ? interestParam : INTERESTS[0],
    message: "",
    company: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Enquiry sent, Kim will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-mist grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-44 pb-16 md:pb-20">
          <p className="label-text text-ocean" data-testid="contact-eyebrow">Get in touch</p>
          <h1
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight"
            data-testid="contact-heading"
          >
            <MaskedLines lines={["Let's start", "the conversation."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70"
          >
            Book a free 30-minute chat, or send an enquiry below, every message
            lands directly with Kim.
          </motion.p>
        </div>
      </section>

      <section className="bg-white" data-testid="contact-content-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28">
          <div className="grid gap-16 lg:grid-cols-12">
            {/* FORM */}
            <div className="lg:col-span-6">
              <Reveal>
                <h2 className="font-display text-3xl md:text-4xl">Send an enquiry</h2>
                {sent ? (
                  <div
                    className="mt-8 border border-ocean/30 bg-mist p-8"
                    data-testid="contact-success-message"
                  >
                    <p className="font-display text-2xl italic text-ocean">Thank you.</p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/70">
                      Your enquiry has been sent, Kim will get back to you
                      personally as soon as she can. Keep an eye on your inbox for
                      a confirmation.
                    </p>
                    <a
                      href={LINKS.calendly}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-success-calendly-link"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors hover:text-ocean"
                    >
                      Or book a call right now
                    </a>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="first-name" className="label-text mb-2 block text-ink/60">
                          First name
                        </label>
                        <input
                          id="first-name"
                          data-testid="contact-first-name-input"
                          required
                          value={form.first_name}
                          onChange={update("first_name")}
                          className={inputCls}
                          placeholder="Jane"
                        />
                      </div>
                      <div>
                        <label htmlFor="last-name" className="label-text mb-2 block text-ink/60">
                          Last name
                        </label>
                        <input
                          id="last-name"
                          data-testid="contact-last-name-input"
                          value={form.last_name}
                          onChange={update("last_name")}
                          className={inputCls}
                          placeholder="Citizen"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="label-text mb-2 block text-ink/60">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        data-testid="contact-email-input"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className={inputCls}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="interest" className="label-text mb-2 block text-ink/60">
                        I'm interested in
                      </label>
                      <select
                        id="interest"
                        data-testid="contact-interest-select"
                        value={form.interest}
                        onChange={update("interest")}
                        className={inputCls}
                      >
                        {INTERESTS.map((i) => (
                          <option key={i} value={i}>{i}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="label-text mb-2 block text-ink/60">
                        Message
                      </label>
                      <textarea
                        id="message"
                        data-testid="contact-message-input"
                        required
                        rows={6}
                        value={form.message}
                        onChange={update("message")}
                        className={`${inputCls} resize-y`}
                        placeholder="Tell me a little about where you're at and what you're looking for…"
                      />
                    </div>
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="hidden"
                      value={form.company}
                      onChange={update("company")}
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-ocean disabled:opacity-60"
                    >
                      {sending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Send size={16} strokeWidth={1.5} />
                      )}
                      {sending ? "Sending…" : "Send enquiry"}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>

            {/* CALENDLY + DIRECT */}
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <h2 className="font-display text-3xl md:text-4xl">Book a free 30-min chat</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">
                  The fastest way to get started, pick a time that suits you.
                </p>
                <div
                  className="mt-8 overflow-hidden border border-ink/10"
                  data-testid="calendly-embed-wrapper"
                >
                  <iframe
                    title="Book a call with The Strength Movement"
                    src="https://calendly.com/thestrengthmovementt/30min?hide_gdpr_banner=1&background_color=ffffff&text_color=0a0a0a&primary_color=2b5b84"
                    className="h-[620px] w-full"
                    loading="lazy"
                  />
                </div>

                <div className="mt-10 border-t border-ink/10 pt-8">
                  <p className="label-text text-ink/50">Elsewhere</p>
                  <div className="mt-5 flex flex-col gap-4">
                    <a
                      href={LINKS.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram-link"
                      className="inline-flex items-center gap-3 text-sm font-semibold text-ink transition-colors hover:text-ocean"
                    >
                      <Instagram size={18} strokeWidth={1.5} className="text-ocean" />
                      @the_strength.movement
                    </a>
                    <a
                      href={LINKS.podcast}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-podcast-link"
                      className="inline-flex items-center gap-3 text-sm font-semibold text-ink transition-colors hover:text-ocean"
                    >
                      <Podcast size={18} strokeWidth={1.5} className="text-ocean" />
                      The Strength Movement Podcast
                    </a>
                    <a
                      href={LINKS.emailList}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-email-list-link"
                      className="inline-flex items-center gap-3 text-sm font-semibold text-ink transition-colors hover:text-ocean"
                    >
                      <Mail size={18} strokeWidth={1.5} className="text-ocean" />
                      Join the email list
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
